<script>
  import { tick } from "svelte"
  import { Turbo } from "@hotwired/turbo-rails"
  import debounce from "debounce"
  import { Search, LoaderCircle, Ellipsis } from "lucide-svelte"
  import Button from "@components/button.svelte"
  import Modal from "@components/shared/modal.svelte"

  let {
    animalId,
    animalName = null,
    showButton = true,
    buttonStyle = "icon",
    placeholder = "Rechercher un acte ou produit..."
  } = $props()

  let open = $state(false)
  let inputRef = $state(null)
  let query = $state("")
  let results = $state([])
  let loading = $state(false)
  let typing = $state(false)
  let requestSequence = 0

  let title = $derived(animalName ? `Rechercher dans l'historique de ${animalName}` : "Rechercher dans l'historique")
  let hasQuery = $derived(query.trim().length > 0)
  let debouncedSearch = debounce(searchItems, 300)

  const tableHeaderClass = "sticky top-0 z-[1] border border-stone-200/60 bg-stone-50/80 px-5 py-[0.8rem] text-left text-smaller font-semibold text-gray-500"
  const tableCellClass = "h-[50px] border border-gray-100 px-5 align-middle text-smaller text-gray-500"
  const linkClass = "cursor-pointer text-left text-gray-500 hover:text-gray-700 hover:underline"

  function openModal() {
    open = true
    tick().then(() => inputRef?.focus?.())
  }

  function resetSearch() {
    requestSequence += 1
    query = ""
    results = []
    loading = false
    typing = false
  }

  function handleClose() {
    resetSearch()
  }

  function handleInput() {
    if (!hasQuery) {
      resetSearch()
      return
    }

    typing = true
    debouncedSearch()
  }

  async function searchItems() {
    const requestedQuery = query.trim()
    if (!requestedQuery) return resetSearch()

    const requestId = ++requestSequence
    typing = false
    loading = true

    const fetchUrl = new URL(`/api/v1/animals/${animalId}/search`, window.location.origin)
    fetchUrl.searchParams.set("q", requestedQuery)
    fetchUrl.searchParams.set("limit", "25")

    try {
      const response = await fetch(fetchUrl.toString())
      const data = await response.json()
      if (requestId !== requestSequence || requestedQuery !== query.trim()) return
      results = data
    } catch (error) {
      if (requestId !== requestSequence) return
      console.error(error)
      results = []
    } finally {
      if (requestId === requestSequence) loading = false
    }
  }

  function visitPath(path) {
    open = false
    resetSearch()
    if (path) Turbo.visit(path)
  }

  function articleLabel(article) {
    if (article?.type === "act") return "Acte"
    if (article?.type === "product") return "Produit"
    return "Article"
  }

  function resourceLabel(resource) {
    if (!resource) return null

    if (resource.type === "consultation") return `Consultation#${resource.id}`
    if (resource.type === "sale") return `Vente#${resource.id}`
    return `#${resource.id}`
  }

  function formatDate(value) {
    if (!value) return "-"
    const [year, month, day] = value.split("-")
    return [day, month, year].filter(Boolean).join("/")
  }
</script>

{#snippet searchContents()}
  <div class="w-full">
    <div class="flex h-10 w-full items-center gap-2 rounded-sm border border-slate-200 px-3 text-slate-400 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
      <Search size={15} strokeWidth={1.75} />
      <input
        bind:this={inputRef}
        bind:value={query}
        oninput={handleInput}
        id="animal-item-search-input"
        {placeholder}
        autocomplete="off"
        class="min-w-0 flex-1 border-0! bg-transparent! p-0! text-[0.85rem] text-slate-500 shadow-none! outline-none! placeholder:text-slate-400 focus:border-0! focus:ring-0! focus:shadow-none!"
      />
      {#if loading || typing}
        {#if loading && results.length === 0}
          <LoaderCircle size={15} class="animate-spin" />
        {:else}
          <Ellipsis size={15} />
        {/if}
      {/if}
    </div>

    {#if hasQuery}
      <div class="mt-8 max-h-96 overflow-auto rounded-sm border border-gray-100">
        {#if loading && results.length === 0}
          <p class="flex items-center gap-2 p-4 text-[0.82rem] text-slate-400">
            <LoaderCircle size={16} class="animate-spin" />
            Chargement...
          </p>
        {:else if results.length === 0}
          <p class="flex items-center gap-2 p-4 text-[0.82rem] text-slate-400">Aucun résultat</p>
        {:else}
          <div class="relative">
            {#if loading}
              <div class="absolute inset-0 z-[2] flex items-center justify-center gap-2 bg-white/80 text-smaller text-gray-400">
                <LoaderCircle size={16} class="animate-spin" />
                Chargement...
              </div>
            {/if}
            <table class={`w-full table-auto border-collapse ${loading ? "opacity-50" : ""}`}>
              <thead>
                <tr>
                  <th class={`${tableHeaderClass} w-28`}>Date</th>
                  <th class={tableHeaderClass}>Article</th>
                  <th class={`${tableHeaderClass} w-20`}>Qté</th>
                  <th class={`${tableHeaderClass} w-52`}>Ressource</th>
                </tr>
              </thead>
              <tbody>
                {#each results as item (item.id)}
                  <tr class="bg-white hover:bg-stone-50/60">
                    <td class={`${tableCellClass} w-28 whitespace-nowrap`}>
                      {#if item.resource?.path}
                        <button type="button" class={linkClass} onclick={() => visitPath(item.resource.path)}>
                          {formatDate(item.date)}
                        </button>
                      {:else}
                        {formatDate(item.date)}
                      {/if}
                    </td>
                    <td class={tableCellClass}>
                      <span class="text-gray-400">{articleLabel(item.article)}:</span>
                      {#if item.article?.path}
                        <button type="button" class={linkClass} onclick={() => visitPath(item.article.path)}>
                          {item.article?.title}
                        </button>
                      {:else}
                        {item.article?.title || "-"}
                      {/if}
                    </td>
                    <td class={`${tableCellClass} w-20 whitespace-nowrap`}>{item.quantity}</td>
                    <td class={`${tableCellClass} w-52 whitespace-nowrap`}>
                      {#if item.resource?.path && resourceLabel(item.resource)}
                        <button type="button" class={linkClass} onclick={() => visitPath(item.resource.path)}>
                          {resourceLabel(item.resource)}
                        </button>
                      {:else}
                        {resourceLabel(item.resource) || "-"}
                      {/if}
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </div>
    {/if}
  </div>
{/snippet}

{#if showButton}
  {#if buttonStyle === "icon"}
    <Button naked hint="Rechercher dans l'historique" onclick={openModal} class="justify-center text-slate-400 hover:text-slate-700">
      <Search size={14} strokeWidth={1.75} />
    </Button>
  {:else}
    <Button naked={buttonStyle === "menu"} onclick={openModal} class={buttonStyle === "menu" ? "flex-row menu-item cursor-pointer" : "button"}>
      <Search size={16} class={buttonStyle === "menu" ? "text-gray-400 mr-2" : "opacity-60"} />
      Rechercher dans l'historique
    </Button>
  {/if}
{/if}

<Modal bind:open {title} width="60rem" padding="px-10 py-8" onClose={handleClose}>
  {@render searchContents()}
</Modal>
